import { getAccessToken } from "api/common";
import { log } from "logging/datadogBrowserLogs";
import {
  MessageHandlersForBackend,
  PortMessageEventData,
  SharedWorkerReturnMessageData,
  WebsocketTopic,
  ValidTopicsForBackend,
  BackendIdentifier,
  WorkerPortId,
} from "websocket/websocketSharedWorkerTypes";

type MessageListener<Backend extends BackendIdentifier> = (
  event: MessageEvent<
    SharedWorkerReturnMessageData<ValidTopicsForBackend[Backend]>
  >,
) => void;

const JITTER_IN_MS = 100;
export function addJitter(jitter: number = JITTER_IN_MS): number {
  // Return jitter in ms, symmetrical around 0
  return Math.floor(Math.random() * jitter - jitter / 2);
}

export function makePortMessageListener<Backend extends BackendIdentifier>(
  sharedWorker: SharedWorker,
  setPortId: (id: WorkerPortId | null) => void,
  messageHandlers: Partial<MessageHandlersForBackend[Backend]>,
): MessageListener<Backend> {
  return async ({ data }): Promise<void> => {
    const sendSubscribeMessage = (portId: WorkerPortId) => {
      const subscribeEvent: PortMessageEventData = {
        type: "add",
        topics: new Set(Object.keys(messageHandlers) as WebsocketTopic[]),
        id: portId,
      };

      sharedWorker.port.postMessage(subscribeEvent);
    };

    const sendTokenMessage = async () => {
      const token = await getAccessToken();

      const tokenEvent: PortMessageEventData = {
        type: "token",
        token,
      };

      sharedWorker.port.postMessage(tokenEvent);
    };

    switch (data.type) {
      case "connected": {
        const { id } = data;
        // The worker has confirmed that the connection is set up, we can now subscribe
        sendSubscribeMessage(id);
        setPortId(id);
        return;
      }

      case "resubscribe": {
        // Worker has told us to resubscribe – probably along with every other port in this tab.
        // Try not to barrage it too hard
        window.setTimeout(
          () => sendSubscribeMessage(data.id),
          75 + addJitter(), // 25–125 ms
        );

        return;
      }

      case "uninitialised": {
        // The worker is connected but the websocket is not – give it a token so it can set up
        await sendTokenMessage();

        // Resubmit the original message
        window.setTimeout(
          () => sharedWorker.port.postMessage(data.original),
          500 + addJitter(),
        );

        return;
      }

      case "reauthenticate": {
        // Worker needs new token to set up websocket
        await sendTokenMessage();

        return;
      }

      case "message": {
        // Received a message on one of the subscribed topics
        // We can assume that message is the correct subtype here to equal MessageData
        // because we have type-guards on valid topics for each backend in useWebsocket :)
        const topic = data.messageContent.topic as keyof typeof messageHandlers;
        messageHandlers[topic]?.(data.messageContent);
        return;
      }

      case "log": {
        // Worker needs to log something
        const { logId, portId, logData } = data;

        // Ack the message immediately
        const logAckMessage: PortMessageEventData = {
          type: "log-ack",
          logId,
          portId,
        };
        sharedWorker.port.postMessage(logAckMessage);

        const { level, component, message, error } = logData;
        log(level, component, message, error);
      }
    }
  };
}
